import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSyndicatePots } from "../../../api/admin";
import { fetchClubs } from "../../../api/players";
import { Player } from "../../../api/players";
import { SyndicatePot } from "../../../api/syndicate";
import { PotCard, LocalPick, EMPTY_PICK } from "../../../pages/SyndicatePage";
import { inputStyle } from "../../../styles";

export interface SyndicatePicksValue {
  picks: { potId: string; clubId?: string | null; playerId?: string | null }[];
  tiebreaker?: number;
  /** Every pot has a real value (not an explicit clear) and a tiebreaker is set — the same
   * bar the backend's syndicatePicksAreComplete enforces, computed here so the parent form
   * can gate its submit button without re-deriving pot count itself. */
  isComplete: boolean;
}

export interface InitialSyndicatePick {
  potId: string;
  clubId?: string | null;
  playerId?: string | null;
  playerName?: string | null;
  playerClub?: string | null;
  playerPosition?: string | null;
}

interface Props {
  user: User;
  competitionId: string;
  onChange: (value: SyndicatePicksValue) => void;
  /** Prefills every pot with the member's real current pick + tiebreaker when editing an
   * existing member — omitted for the add-member backfill flow, which starts empty. */
  initialPicks?: InitialSyndicatePick[];
  initialTiebreaker?: number;
}

/**
 * Admin-only pot picker for backfilling a SYNDICATE member's picks + tiebreaker after
 * their deadline has passed, or editing an existing member's picks mid-season. Reuses
 * PotCard and the LocalPick shape exported from pages/SyndicatePage.tsx (the same
 * accordion card every member uses to make picks) — forced always-editable here
 * (isLocked/isMemberView both false) since bypassing the lock is the entire point of this
 * form. Pot data comes from the admin pot-config endpoint (fetchSyndicatePots) rather than
 * the member-scoped picks endpoint, since there is no member yet to scope a fetch to for
 * the add-member flow (the edit flow's initial values are passed in as props instead).
 */
export default function SyndicatePotsPicker({ user, competitionId, onChange, initialPicks, initialTiebreaker }: Props) {
  const { t } = useTranslation();
  const [pots, setPots] = useState<SyndicatePot[]>([]);
  const [clubNames, setClubNames] = useState<string[]>([]);
  const [picks, setPicks] = useState<Record<string, LocalPick>>(() => {
    const seeded: Record<string, LocalPick> = {};
    for (const ip of initialPicks ?? []) {
      seeded[ip.potId] = {
        clubId: ip.clubId ?? null,
        playerId: ip.playerId ?? null,
        playerName: ip.playerName ?? null,
        playerClub: ip.playerClub ?? null,
        playerPosition: ip.playerPosition ?? null,
      };
    }
    return seeded;
  });
  const [tiebreaker, setTiebreaker] = useState(initialTiebreaker !== undefined ? String(initialTiebreaker) : "");
  const [openPotId, setOpenPotId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seededRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchSyndicatePots(user, competitionId), fetchClubs(user, competitionId)])
      .then(([potsRes, clubsRes]) => {
        if (cancelled) return;
        const adapted: SyndicatePot[] = potsRes.pots.map((p) => ({
          id: p.id, order: p.order, kind: p.kind, label: p.label,
          winPoints: p.winPoints, drawPoints: p.drawPoints,
          eligibleClubs: p.clubs, statType: p.statType, pointsPer: p.pointsPer,
          excludedPlayers: p.excludedPlayers,
        }));
        setPots(adapted);
        setClubNames(clubsRes);
      })
      .catch(() => { /* ignore — empty pot list surfaces as a backend validation error on submit */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, competitionId]);

  // Separate effect, not folded into the fetch above: calling the parent's onChange
  // synchronously inside that effect's `.then()` raced React's initial-commit warning
  // ("Cannot update a component while rendering a different component") under a mocked
  // promise that resolves before the mount finishes flushing. Keying this off `pots`
  // itself defers the emit to its own, correctly-ordered effect pass. The ref makes it
  // fire exactly once — after that, `selectClub`/`clearPick`/`changeTiebreaker` own emitting.
  useEffect(() => {
    if (seededRef.current || pots.length === 0) return;
    if (!initialPicks && initialTiebreaker === undefined) return;
    seededRef.current = true;
    emit(picks, tiebreaker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pots]);

  const emit = (nextPicks: Record<string, LocalPick>, nextTiebreaker: string) => {
    const filledEntries = Object.entries(nextPicks).filter(([, pick]) => pick.clubId || pick.playerId);
    const picksPayload = filledEntries.map(([potId, pick]) => ({ potId, clubId: pick.clubId, playerId: pick.playerId }));
    const parsedTiebreaker = nextTiebreaker.trim() === "" ? undefined : parseInt(nextTiebreaker, 10);
    const tiebreaker = Number.isNaN(parsedTiebreaker) ? undefined : parsedTiebreaker;
    const isComplete = pots.length > 0 && filledEntries.length === pots.length && tiebreaker !== undefined;
    onChange({ picks: picksPayload, tiebreaker, isComplete });
  };

  const selectClub = (potId: string, clubId: string) => {
    setPicks((prev) => {
      const next = { ...prev, [potId]: { ...EMPTY_PICK, clubId } };
      emit(next, tiebreaker);
      return next;
    });
  };

  const selectPlayer = (potId: string, player: Player) => {
    setPicks((prev) => {
      const next = { ...prev, [potId]: { clubId: null, playerId: player.id, playerName: player.name, playerClub: player.club, playerPosition: player.position } };
      emit(next, tiebreaker);
      return next;
    });
  };

  const clearPick = (potId: string) => {
    setPicks((prev) => {
      const next = { ...prev, [potId]: EMPTY_PICK };
      emit(next, tiebreaker);
      return next;
    });
  };

  const changeTiebreaker = (value: string) => {
    setTiebreaker(value);
    emit(picks, value);
  };

  if (loading) return <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>{t("common.loading")}</p>;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.6rem", marginBottom: "0.8rem" }}>
        {pots.map((pot, i) => (
          <PotCard
            key={pot.id}
            pot={pot}
            index={i + 1}
            pick={picks[pot.id] ?? EMPTY_PICK}
            isLocked={false}
            isMemberView={false}
            points={0}
            form={null}
            statCount={null}
            invalid={false}
            competitionId={competitionId}
            clubs={clubNames}
            user={user}
            isMobile={false}
            isOpen={openPotId === pot.id}
            onToggle={() => setOpenPotId((cur) => (cur === pot.id ? null : pot.id))}
            onSelectClub={(clubId) => selectClub(pot.id, clubId)}
            onSelectPlayer={(player) => selectPlayer(pot.id, player)}
            onClear={() => clearPick(pot.id)}
          />
        ))}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem" }}>
        {t("syndicate.stats.tiebreaker")}
        <input
          type="number"
          min={0}
          style={{ ...inputStyle, width: 90 }}
          value={tiebreaker}
          onChange={(e) => changeTiebreaker(e.target.value)}
        />
      </label>
    </div>
  );
}
